<template>
  <v-navigation-drawer
    v-model="isVisible"
    right
    width="560"
    fixed
    temporary
    class="agent-settings-drawer">
    <!-- Header -->
    <div class="drawer-header">
      <div class="header-content">
        <h2 class="drawer-title">
          Settings
        </h2>
        <v-btn
          icon
          class="close-btn"
          @click="onClose">
          <Icon name="cross" variant="solid" size="20"/>
        </v-btn>
      </div>
    </div>

    <!-- Tabs -->
    <div class="tabs-area">
      <div class="tabs-container">
        <v-btn
          v-for="tab in tabs"
          :key="tab.key"
          :class="['tab-btn', { 'active': activeTab === tab.key }]"
          text
          elevation="0"
          @click="activeTab = tab.key">
          <div>
            {{ tab.label }}
          </div>
        </v-btn>
      </div>
    </div>

    <!-- Content -->
    <div class="drawer-content">
      <!-- General Tab -->
      <div v-if="activeTab === 'general'" class="tab-content">
        <div class="form-group">
          <label class="form-label">Agent Model</label>
          <MankSelect
            v-model="settings.model"
            :items="modelOptions"
            class="voice-select"
            :menu-props="{
              maxHeight:300,
              offsetY:true,
              nudgeBottom:8,
            }"
            item-text="label"
            item-value="value">
            <template v-slot:append>
              <Icon name="expand-down" variant="solid" color="muted"/>
            </template>
          </MankSelect>
        </div>

        <div class="form-group">
          <label class="form-label">Initial Output Medium</label>
          <MankSelect
            v-model="settings.initialOutputMedium"
            :items="outputMediumOptions"
            placeholder="Select Initial Output Medium"
            class="voice-select"
            :menu-props="{
              maxHeight:300,
              offsetY:true,
              nudgeBottom:8,
            }"
            item-text="label"
            item-value="value">
            <template v-slot:append>
              <Icon name="expand-down" variant="solid" color="muted"/>
            </template>
          </MankSelect>
        </div>

        <div class="form-group">
          <label class="form-label">Join Timeout (s)</label>
          <MankTextField
            v-model="settings.joinTimeout"
            placeholder="Enter Join Timeout"
            class="form-input"/>
        </div>

        <div class="form-group">
          <label class="form-label">Max Duration (m)</label>
          <MankTextField
            v-model="settings.maxDuration"
            placeholder="Enter Max Duration"
            class="form-input"/>
        </div>

        <div class="form-group">
          <label class="form-label">Time Exceeded Message</label>
          <MankTextarea
            v-model="settings.timeExceededMessage"
            placeholder="Example: Sorry we've reached our time limit."
            variant="outlined"
            rows="3"
            class="system-prompt-textarea"/>
        </div>

        <div class="form-group toggle-group">
          <label class="form-label">Time Exceeded Message</label>
          <div class="custom-toggle" :class="{ 'active': settings.timeExceededEnabled }" @click="settings.timeExceededEnabled = !settings.timeExceededEnabled">
            <div class="toggle-thumb"/>
            <span class="toggle-text">{{ settings.timeExceededEnabled ? 'Enabled' : 'Disabled' }}</span>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Temperature</label>
          <div class="slider-group">
            <v-slider
              v-model="settings.temperature"
              :min="0"
              :max="2"
              :step="0.1"
              color="primary"
              class="form-slider"
              hide-details/>
            <div class="slider-value">
              {{ settings.temperature }}
            </div>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">Language Hint (BCP47 code)</label>
          <MankTextField
            v-model="settings.languageHint"
            placeholder="Example: en-US"
            class="form-input"/>
        </div>
      </div>

      <!-- External TTS Tab -->
      <div v-else-if="activeTab === 'external-tts'" class="tab-content">
        <!-- Info Line -->
        <div class="info-line">
          <div class="info-icon">
            <Icon name="warning-circle" variant="solid" size="16" color="warning"/>
          </div>
          <div class="info-content">
            <p class="info-text">
              To enable external text to speech services, an external API key may be required.
            </p>
            <a href="#" class="info-link">
              Add External API
              <Icon name="arrow-right" variant="solid" size="20" color="tertiary"/>
            </a>
          </div>
        </div>

        <!-- Provider Dropdown -->
        <div class="form-group">
          <label class="form-label">Provider</label>
          <MankSelect
            v-model="settings.ttsProvider"
            :items="ttsProviderOptions"
            class="voice-select"
            :menu-props="{
              maxHeight:300,
              offsetY:true,
              nudgeBottom:8,
            }"
            item-text="title"
            item-value="value">
            <template v-slot:append>
              <Icon name="expand-down" variant="solid" color="muted"/>
            </template>
          </MankSelect>
        </div>

        <!-- Code Area -->
        <div class="code-area">
          <!-- Code editor placeholder -->
        </div>
      </div>

      <!-- First Speaker Tab -->
      <div v-else-if="activeTab === 'first-speaker'" class="tab-content">
        <!-- First Speaker Dropdown -->
        <div class="form-group">
          <label class="form-label">First Speaker</label>
          <MankSelect
            v-model="settings.firstSpeaker"
            :items="firstSpeakerOptions"
            class="voice-select"
            :menu-props="{
              maxHeight:300,
              offsetY:true,
              nudgeBottom:8,
            }"
            item-text="title"
            item-value="value">
            <template v-slot:append>
              <Icon name="expand-down" variant="solid" color="muted"/>
            </template>
          </MankSelect>
        </div>

        <!-- First Message / Greeting -->
        <div class="form-group">
          <label class="form-label">First Message / Greeting</label>
          <MankTextarea
            v-model="settings.firstMessage"
            placeholder="Example: Hello! How can I help you today?"
            variant="outlined"
            rows="3"
            class="system-prompt-textarea"/>
        </div>

        <!-- Uninterruptible Toggle -->
        <div class="form-group toggle-group">
          <label class="form-label">Uninterruptible</label>
          <div class="custom-toggle" :class="{ 'active': settings.uninterruptible }" @click="settings.uninterruptible = !settings.uninterruptible">
            <div class="toggle-thumb"/>
            <span class="toggle-text">{{ settings.uninterruptible ? 'Enabled' : 'Disabled' }}</span>
          </div>
        </div>

        <!-- First Message Delay -->
        <div class="form-group">
          <label class="form-label">First Message Delay (s)</label>
          <MankTextField
            v-model="settings.firstMessageDelay"
            placeholder="Enter First Message Delay"
            class="form-input"/>
        </div>
      </div>

      <!-- Inactivity Messages Tab -->
      <div v-else-if="activeTab === 'inactivity-messages'" class="tab-content">
        <!-- Add Message Button -->
        <div class="add-message-btn" @click="addInactivityMessage">
          <Icon name="plus" variant="solid" size="20" color="default"/>
          <span class="add-message-text">Add Message</span>
        </div>

        <!-- Inactivity Messages List -->
        <div v-if="settings.inactivityMessages.length > 0" class="messages-list">
          <div
            v-for="(message, index) in settings.inactivityMessages"
            :key="index"
            class="message-card">
            <!-- Card Header -->
            <div class="card-header">
              <h3 class="message-title">
                Message {{ index + 1 }}
              </h3>
            </div>

            <!-- Card Content -->
            <div class="card-content">
              <!-- Duration Input -->
              <div class="form-group">
                <label class="form-label required">
                  Duration (s)
                  <span class="required-asterisk">*</span>
                </label>
                <MankTextField
                  v-model="message.duration"
                  placeholder="Enter Duration"
                  class="form-input"/>
              </div>

              <!-- Message Textarea -->
              <div class="form-group">
                <label class="form-label required">
                  Message
                  <span class="required-asterisk">*</span>
                </label>
                <MankTextarea
                  v-model="message.text"
                  placeholder="Example: Sorry, we've reached our time limit and I have to end this call. Thank you for calling."
                  variant="outlined"
                  rows="3"
                  class="system-prompt-textarea"/>
              </div>

              <!-- End Behavior Dropdown -->
              <div class="form-group">
                <label class="form-label required">
                  End Behavior
                  <span class="required-asterisk">*</span>
                </label>
                <MankSelect
                  v-model="message.endBehavior"
                  :items="endBehaviorOptions"
                  placeholder="Select End Behavior"
                  class="voice-select"
                  :menu-props="{
                    maxHeight:300,
                    offsetY:true,
                    nudgeBottom:8,
                  }"
                  item-text="title"
                  item-value="value">
                  <template v-slot:append>
                    <Icon name="expand-down" variant="solid" color="muted"/>
                  </template>
                </MankSelect>
              </div>
            </div>

            <!-- Card Footer -->
            <div class="card-footer">
              <v-btn
                color="surface-1"
                size="large"
                block
                elevation="0"
                class="cancel-btn mank-btn"
                @click="removeInactivityMessage(index)">
                <Icon name="trash" variant="solid" size="20" color="default"/>
                Delete
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <!-- Voice Activity Detection Tab -->
      <div v-else-if="activeTab === 'voice-activity-detection'" class="tab-content">
        <!-- Warning Info Line -->
        <div class="info-line ">
          <div class="info-icon">
            <Icon name="warning-circle" variant="solid" size="16" color="warning"/>
          </div>
          <span class="info-text">
            Changing these settings could drastically alter your agent's performance.
          </span>
        </div>

        <!-- Turn Endpoint Delay -->
        <div class="form-group">
          <label class="form-label">Turn Endpoint Delay (ms)</label>
          <MankTextField
            v-model="settings.turnEndpointDelay"
            class="form-input filled-input"/>
        </div>

        <!-- Minimum Turn Duration -->
        <div class="form-group">
          <label class="form-label">Minimum Turn Duration (ms)</label>
          <MankTextField
            v-model="settings.minimumTurnDuration"
            class="form-input filled-input"/>
        </div>

        <!-- Minimum Interruption Duration -->
        <div class="form-group">
          <label class="form-label">Minimum Interruption Duration (ms)</label>
          <MankTextField
            v-model="settings.minimumInterruptionDuration"
            class="form-input filled-input"/>
        </div>

        <!-- Frame Activation Threshold -->
        <div class="form-group">
          <label class="form-label">Frame Activation Threshold</label>
          <div class="slider-group">
            <v-slider
              v-model="settings.frameActivationThreshold"
              :min="0"
              :max="2"
              :step="0.1"
              color="primary"
              class="form-slider"
              hide-details/>
            <div class="slider-value">
              {{ settings.frameActivationThreshold.toFixed(1) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="drawer-footer">
      <div class="button-group">
        <v-btn
          color="surface-1"
          size="large"
          elevation="0"
          class="cancel-btn mank-btn"
          @click="cancelChanges">
          <Icon name="cross" variant="solid" color="default" size="20"/>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          size="large"
          elevation="0"
          class="apply-btn mank-btn"
          @click="applyChanges">
          <Icon name="check-circle" variant="solid" color="onPrimary" size="20"/>
          Apply
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import Icon from '@/components/Icon.vue';
import AgentService from '@/services/AgentService';

export default {
  name: 'AgentSettingsDrawer',
  components: { Icon },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    agentData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'apply', 'cancel'],
  data() {
    return {
      activeTab: 'general',
      settings: {
        model: 'Fixie-ai/mank',
        initialOutputMedium: '',
        joinTimeout: '',
        maxDuration: '',
        timeExceededMessage: '',
        timeExceededEnabled: false,
        temperature: 0.0,
        languageHint: '',
        ttsProvider: 'Generic',
        firstSpeaker: 'Agent',
        firstMessage: '',
        uninterruptible: false,
        firstMessageDelay: '',
        inactivityMessages: [],
        // Voice Activity Detection
        turnEndpointDelay: '394',
        minimumTurnDuration: '0',
        minimumInterruptionDuration: '90',
        frameActivationThreshold: 0.0,
      },
      originalSettings: {},
      tabs: [
        { key: 'general', label: 'General' },
        { key: 'external-tts', label: 'External\nTTS' },
        { key: 'first-speaker', label: 'First\nSpeaker' },
        { key: 'inactivity-messages', label: 'Inactivity Messages' },
        { key: 'voice-activity-detection', label: 'Voice Activity Detection' },
      ],
      modelOptions: [
        { label: 'Fixie-ai/mank', value: 'Fixie-ai/mank' },
        { label: 'GPT-4', value: 'gpt-4' },
        { label: 'GPT-3.5 Turbo', value: 'gpt-3.5-turbo' },
      ],
      outputMediumOptions: [
        { label: 'Voice', value: 'audio' },
        { label: 'Text', value: 'text' },
      ],
      ttsProviderOptions: [
        { title: 'Generic', value: 'Generic' },
        { title: 'OpenAI', value: 'OpenAI' },
        { title: 'ElevenLabs', value: 'ElevenLabs' },
        { title: 'Azure', value: 'Azure' },
      ],
      firstSpeakerOptions: [
        { title: 'Agent', value: 'Agent' },
        { title: 'User', value: 'User' },
        { title: 'System', value: 'System' },
      ],
      endBehaviorOptions: [
        { title: 'Continue', value: 'continue' },
        { title: 'End Call', value: 'end_call' },
        { title: 'Transfer', value: 'transfer' },
        { title: 'Voicemail', value: 'voicemail' },
      ],
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      },
    },
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        // Drawer açıldığında ayarları yükle
        this.loadSettingsFromAgentData();
        this.originalSettings = { ...this.settings };
        // Agent models'ı yükle
        this.loadAgentModels();
      }
    },
    agentData: {
      handler() {
        this.loadSettingsFromAgentData();
      },
      deep: true,
    },
  },
  methods: {
    loadSettingsFromAgentData() {
      if (this.agentData) {
        const modelId = this.agentData.agent_model_id;
        this.settings.model = (modelId && typeof modelId === 'object' ? modelId.id : modelId) || '';
        this.settings.initialOutputMedium = this.agentData.initial_output_medium || 'audio';
        // Diğer ayarları da map edebiliriz
        this.settings.temperature = this.agentData.temperature || 0.7;
        this.settings.maxDuration = this.agentData.max_duration || 300;
        this.settings.joinTimeout = this.agentData.join_timeout || 30;
        this.settings.timeExceededMessage = this.agentData.time_exceed_message || '';
        this.settings.languageHint = this.agentData.language_hint || 'en';
        this.settings.firstMessage = this.agentData.first_message || '';
        this.settings.firstSpeaker = this.agentData.first_speaker || 'agent';
        this.settings.firstMessageDelay = this.agentData.first_message_delay || 0;
        this.settings.uninterruptible = this.agentData.uninterruptible || false;
        this.settings.frameActivationThreshold = this.agentData.frame_activation_threshold || 0.5;
        this.settings.minimumInterruptionDuration = this.agentData.minimum_interruption_duration || 0.5;
        this.settings.minimumTurnDuration = this.agentData.minimum_turn_duration || 0.5;
        this.settings.turnEndpointDelay = this.agentData.turn_endpoint_delay || 0.5;
        this.settings.inactivityMessages = this.agentData.inactivity_messages || [];
      }
    },
    async loadAgentModels() {
      try {
        const response = await AgentService.getAgentModels({ source: 'AGENT' });
        const raw = response?.success && Array.isArray(response.data) ? response.data : null;
        if (raw) {
          this.modelOptions = raw
            .map((model) => {
              const id = model.id ?? model.ID;
              const name = model.name ?? model.Name ?? '';
              return {
                label: name || id || 'Agent Model',
                value: id,
              };
            })
            .filter((o) => o.value != null && o.value !== '');
        }
      } catch (error) {
        // Hata durumunda default model options kullanılacak
      } finally {
        this.applyDefaultModelWhenCreating();
      }
    },
    /**
     * Yeni ajan (kayıtlı model yok): liste geldikten sonra ilk modeli seç.
     * Düzenlemede mevcut id listede geçerliyse dokunulmaz.
     */
    applyDefaultModelWhenCreating() {
      const mid = this.agentData?.agent_model_id;
      const savedId = mid && typeof mid === 'object' ? mid.id : mid;
      if (savedId) return;
      if (!this.modelOptions.length) return;
      const current = this.settings.model;
      const stillValid = current && this.modelOptions.some((o) => o.value === current);
      if (stillValid) return;
      this.settings.model = this.modelOptions[0].value;
      this.originalSettings = { ...this.originalSettings, model: this.settings.model };
    },
    onClose() {
      this.$emit('close');
    },
    cancelChanges() {
      // Orijinal ayarlara geri dön
      this.settings = { ...this.originalSettings };
      this.$emit('cancel');
      this.$emit('close');
    },
    applyChanges() {
      // Settings'i agentData formatına çevir
      const updatedAgentData = {
        agent_model_id: this.settings.model,
        initial_output_medium: this.settings.initialOutputMedium,
        temperature: this.settings.temperature,
        max_duration: this.settings.maxDuration,
        join_timeout: this.settings.joinTimeout,
        time_exceed_message: this.settings.timeExceededMessage,
        language_hint: this.settings.languageHint,
        first_message: this.settings.firstMessage,
        first_speaker: this.settings.firstSpeaker,
        first_message_delay: this.settings.firstMessageDelay,
        uninterruptible: this.settings.uninterruptible,
        frame_activation_threshold: this.settings.frameActivationThreshold,
        minimum_interruption_duration: this.settings.minimumInterruptionDuration,
        minimum_turn_duration: this.settings.minimumTurnDuration,
        turn_endpoint_delay: this.settings.turnEndpointDelay,
        inactivity_messages: this.settings.inactivityMessages,
      };
      this.$emit('apply', updatedAgentData);
      this.$emit('close');
    },
    addInactivityMessage() {
      this.settings.inactivityMessages.push({
        duration: '',
        text: '',
        endBehavior: '',
      });
    },
    removeInactivityMessage(index) {
      this.settings.inactivityMessages.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.agent-settings-drawer {
  :deep(.v-navigation-drawer__content) {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.drawer-header {
  padding: 12px 24px;
  border-bottom: 1px solid $light-border;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.drawer-title {
  margin: 0;
  color: $light-foreground-1;
  font-family: $body-font-family;
  font-size: $h5-size;
  font-weight: 700;
  line-height: 1.4;
}

.close-btn {
  flex-shrink: 0;
}

.tabs-area {
  border-bottom: 1px solid $light-border;
  flex-shrink: 0;
}

.tabs-container {
  display: flex;
  align-items: stretch;
}

.tab-btn {
  flex: 1;
  height: 40px;
  border-radius: 0;
  border-bottom: 2px solid transparent;
  font-size: $caption-1-size;
  font-weight: 600;
  color: $light-foreground-4;
  background-color: transparent;

  &.active {
    color: $light-foreground-1;
    border-bottom-color: $light-primary;
  }

  &:hover:not(.active) {
    background-color: $light-surface-2;
  }
  div{
    white-space: pre-line;
    width: 112px;
  }
}

.drawer-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.tab-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.toggle-group {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.form-label {
  font-family: $body-font-family;
  font-size: $body-3-size;
  font-weight: 600;
  color: $light-foreground-1;
  margin: 0;
}

.form-select,
.form-input,
.form-textarea {
  :deep(.v-field) {
    background-color: $light-surface-2;
    border-radius: 8px;
  }

  :deep(.v-field__input) {
    font-family: $body-font-family;
    font-size: $body-3-size;
    color: $light-foreground-1;
    padding: 6px 8px;
    min-height: 32px;
  }

  :deep(.v-field__outline) {
    display: none;
  }
}

.form-textarea {
  :deep(.v-field__input) {
    min-height: 60px;
  }

  :deep(.v-field__append-inner) {
    padding-top: 8px;
    padding-right: 8px;
    align-items: flex-start;
  }
}

.custom-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px 3px 3px;
  background-color: #E9EDF1;
  border-radius: 20px;
  width: 85px;
  height: 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  .toggle-thumb {
    width: 18px;
    height: 18px;
    background-color: #FFFFFF;
    border-radius: 50%;
    transition: transform 0.2s ease;
    flex-shrink: 0;
  }

  .toggle-text {
    font-family: $body-font-family;
    font-size: $caption-1-size;
    font-weight: 600;
    color: #5C6A75;
    line-height: 1.2;
  }

  &.active {
    background-color: $light-primary;

    .toggle-thumb {
      transform: translateX(24px);
    }

    .toggle-text {
      color: #FFFFFF;
    }
  }

  &:hover {
    opacity: 0.9;
  }
}

.slider-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-slider {
  flex: 1;

  :deep(.v-slider-track__fill) {
    background-color: $light-primary;
  }

  :deep(.v-slider-thumb__surface) {
    background-color: $light-surface-1;
    border: 2px solid $light-foreground-2;
  }
}

.slider-value {
  width: 48px;
  height: 32px;
  background-color: #FFFFFF;
  border: 1px solid #D3DAE0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: $body-font-family;
  font-size: $body-3-size;
  font-weight: 400;
  color: #93A0AF;
  flex-shrink: 0;
}

.info-line {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 1px solid #EFB424;
  border-radius: 8px;
  background-color: transparent;
  .info-text {
    color:$light-foreground-3;
  }
  .info-icon {
    width: 24px;
    height: 24px;
    background-color: #FDF8E9;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .info-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;

    .info-link {
      display: flex;
      align-items: center;
      gap: 4px;
      font-family: $body-font-family;
      font-size: $caption-1-size;
      font-weight: 600;
      color: $light-foreground-3;
      text-decoration: underline;
      text-decoration-color: $light-foreground-3;
      text-underline-offset: 2px;

      &:hover {
        opacity: 0.8;
      }
    }
  }
}

.code-area {
  width: 100%;
  height: 320px;
  background-color: #171D22;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #93A0AF;
  font-family: $body-font-family;
  font-size: $body-3-size;
}

.add-message-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background-color: #FFFFFF;
  border: 1px dashed #D3DAE0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  .add-message-text {
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    color: #171D22;
  }

  &:hover {
    background-color: $light-surface-2;
    border-color: $light-primary;
  }
}

.messages-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-card {
  background-color: $light-surface-1;
  border-radius: 12px;
  overflow: hidden;

  .card-header {
    padding: 8px 16px;
    border-bottom: 1px solid #D3DAE0;

    .message-title {
      margin: 0;
      font-family: $body-font-family;
      font-size: $body-2-size;
      font-weight: 600;
      color: #171D22;
      line-height: 1.4;
    }
  }

  .card-content {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .form-label.required {
        display: flex;
        align-items: center;
        gap: 4px;
        font-family: $body-font-family;
        font-size: $body-3-size;
        font-weight: 600;
        color: #171D22;

        .required-asterisk {
          color: #EB575D;
          font-weight: 700;
        }
      }
    }
    .mank-textarea,
    .mank-input,
    .mank-select {
      background-color: $light-surface-2;

      ::v-deep .v-input__slot {
        background-color: $light-surface-2 !important;
      }
    }
  }

  .card-footer {
    padding: 4px 16px;
    border-top: 1px solid #D3DAE0;
    display: flex;
    justify-content: center;

    .delete-message-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      font-family: $body-font-family;
      font-size: $body-3-size;
      font-weight: 600;
      color: #171D22;
      text-transform: none;

      &:hover {
        background-color: rgba(235, 87, 93, 0.1);
      }
    }
  }
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: $light-foreground-3;
  font-family: $body-font-family;
  font-size: $body-3-size;
}

.drawer-footer {
  padding: 12px 24px;
  border-top: 1px solid $light-border;
  flex-shrink: 0;
}

.button-group {
  display: flex;
  gap: 12px;

  .cancel-btn,
  .apply-btn {
    flex: 1;
    height: 32px;
  }
}
</style>
